import { entityColorKey, teamGroupColorKey } from "@/analytics/core/chart-palette";
import {
  collectScopedReps,
  dedupeRepOptions,
  expandRepSelection,
} from "@/analytics/core/rep-scope";
import type { ExploreScopeSelection, EntityOption, AnalyticsRawData } from "@/analytics/core/types";
import type { MetricScopeContext } from "@/analytics/core/resolve-metric";
import { buildTeamGroups } from "@/grid/data/assemble-grid";
import type { DepartmentDefinition } from "@/grid/core/types";

export interface EntitySeriesSpec {
  key: string;
  label: string;
  ctx: MetricScopeContext;
}

function repsByTeam(reps: AnalyticsRawData["reps"]): Map<number, number[]> {
  const map = new Map<number, number[]>();
  for (const rep of reps) {
    const list = map.get(rep.team_id) ?? [];
    list.push(rep.id);
    map.set(rep.team_id, list);
  }
  return map;
}

export function listTeamOptions(
  raw: AnalyticsRawData,
  department: DepartmentDefinition,
): EntityOption[] {
  return buildTeamGroups(raw.teams, department).map((group) => ({
    id: group.id,
    name: group.name,
    colorKey: teamGroupColorKey(group.groupKey),
  }));
}

function allowedTeamIdsForSelection(
  groups: ReturnType<typeof buildTeamGroups>,
  teamIds: number[],
): number[] {
  if (teamIds.length === 1) {
    return groups.find((g) => g.id === teamIds[0])?.teamIds ?? [];
  }
  return groups.flatMap((g) => g.teamIds);
}

export function listRepOptions(
  raw: AnalyticsRawData,
  department: DepartmentDefinition,
  teamIds: number[],
): EntityOption[] {
  const groups = buildTeamGroups(raw.teams, department);
  const allowedTeamIds = allowedTeamIdsForSelection(groups, teamIds);
  const scopedReps = collectScopedReps(raw.reps, allowedTeamIds);

  return dedupeRepOptions(scopedReps);
}

export function resolveEntitySeries(
  selection: ExploreScopeSelection,
  raw: AnalyticsRawData,
  department: DepartmentDefinition,
): EntitySeriesSpec[] {
  const groups = buildTeamGroups(raw.teams, department);
  const teamRepMap = repsByTeam(raw.reps);

  if (selection.repIds.length > 0) {
    const allowedTeamIds = allowedTeamIdsForSelection(groups, selection.teamIds);
    const scopedReps = collectScopedReps(raw.reps, allowedTeamIds);

    return expandRepSelection(selection.repIds, scopedReps).map((rep) => ({
      key: entityColorKey("rep", rep.keyRepId),
      label: rep.label,
      ctx: {
        teamIds: rep.teamIds,
        repIds: rep.repIds,
        useRepPeriodGrain: true,
      },
    }));
  }

  if (selection.teamIds.length > 0) {
    return selection.teamIds
      .map((teamGroupId) => groups.find((g) => g.id === teamGroupId))
      .filter((group): group is NonNullable<typeof group> => group != null)
      .map((group) => ({
        key: teamGroupColorKey(group.groupKey),
        label: group.name,
        ctx: {
          teamIds: group.teamIds,
          repIds: group.teamIds.flatMap((teamId) => teamRepMap.get(teamId) ?? []),
          useRepPeriodGrain: false,
        },
      }));
  }

  const teamIds = groups.flatMap((group) => group.teamIds);
  return [
    {
      key: "all-teams",
      label: "All teams",
      ctx: {
        teamIds,
        repIds: teamIds.flatMap((teamId) => teamRepMap.get(teamId) ?? []),
        useRepPeriodGrain: false,
      },
    },
  ];
}

export function teamPickerLabel(selection: ExploreScopeSelection, teams: EntityOption[]): string {
  if (selection.teamIds.length === 0) return "All teams";
  if (selection.teamIds.length === 1) {
    return teams.find((team) => team.id === selection.teamIds[0])?.name ?? "1 team";
  }
  return `${selection.teamIds.length} teams`;
}

export function repPickerLabel(selection: ExploreScopeSelection, reps: EntityOption[]): string {
  if (selection.repIds.length === 0) return "All reps";
  if (selection.repIds.length === 1) {
    return reps.find((rep) => rep.id === selection.repIds[0])?.name ?? "1 rep";
  }
  return `${selection.repIds.length} reps`;
}

export function showRepPicker(selection: ExploreScopeSelection): boolean {
  return selection.teamIds.length === 1;
}
